// The staff session, held apart from everything the buyer's app knows about itself.
//
// A phone can be both someone's ordering app and their work tool. Those two must never
// be able to stand in for one another, so this session has its own server address, its
// own token and its own device key alias — nothing here reads or writes a contact.

import { getSetting, setSetting, SETTING_STAFF_SERVER, SETTING_STAFF_SESSION } from '$lib/db/settings';
import type { StaffSession } from '$lib/protocol/operator-api';

interface Stored {
  token: string;
  expiresAtMs: number;
  operatorName: string;
}

const state = $state({
  serverUrl: '',
  session: undefined as Stored | undefined,
  loaded: false,
});

export async function loadStaffState(): Promise<void> {
  state.serverUrl = (await getSetting<string>(SETTING_STAFF_SERVER)) ?? '';
  const stored = await getSetting<Stored>(SETTING_STAFF_SESSION);
  // An expired token is no token. Keeping one would only produce a 401 at the first
  // useful moment, which is a worse way to learn the same thing.
  state.session = stored !== undefined && stored.expiresAtMs > Date.now() ? stored : undefined;
  state.loaded = true;
}

export function staffServerUrl(): string {
  return state.serverUrl;
}

export function staffSession(): Stored | undefined {
  return state.session;
}

export function staffLoaded(): boolean {
  return state.loaded;
}

export async function saveStaffSignIn(serverUrl: string, session: StaffSession): Promise<void> {
  state.serverUrl = serverUrl;
  state.session = {
    token: session.token,
    expiresAtMs: session.expiresAtMs,
    operatorName: session.operatorName,
  };
  await setSetting(SETTING_STAFF_SERVER, serverUrl);
  await setSetting(SETTING_STAFF_SESSION, state.session);
}

export async function signOutStaff(): Promise<void> {
  state.session = undefined;
  await setSetting(SETTING_STAFF_SESSION, undefined);
}
